import { LoginFormTypes, LoginPageProps, useLogin, useTranslate } from '@refinedev/core';
import {
  Button,
  Card,
  CardProps,
  Col,
  Divider,
  Form,
  FormProps,
  Input,
  Layout,
  LayoutProps,
  Row,
  theme,
  Typography,
} from 'antd';
import React from 'react';

import acronex_logo_lg from '@/img/acronex-logo-lg.svg';

import { bodyStyles, containerStyles, headStyles, layoutStyles, titleStyles } from './styles';

const { Text, Title } = Typography;
const { useToken } = theme;

type LoginProps = LoginPageProps<LayoutProps, CardProps, FormProps>;

export const LoginPage: React.FC<LoginProps> = ({
  providers,
  contentProps,
  wrapperProps,
  renderContent,
  formProps,
  title,
}) => {
  const { token } = useToken();
  const [form] = Form.useForm<LoginFormTypes>();
  const translate = useTranslate();
  const { mutate: login } = useLogin<LoginFormTypes>();
  const PageTitle =
    title === false ? null : (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '32px',
          fontSize: '20px',
        }}>
        {title ?? (
          <img
            style={{
              width: 250,
            }}
            src={acronex_logo_lg}
            alt="Logo Acronex"
          />
        )}
      </div>
    );
  const CardTitle = (
    <Title
      level={3}
      style={{
        color: token.colorPrimaryTextHover,
        ...titleStyles,
      }}>
      {translate('pages.login.title', 'Sign in to your account')}
    </Title>
  );
  const renderProviders = () => {
    if (providers && providers.length > 0) {
      return (
        <>
          {providers.map(provider => {
            return (
              <Button
                key={provider.name}
                type="default"
                block
                icon={provider.icon}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  marginBottom: '8px',
                }}
                onClick={() =>
                  login({
                    providerName: provider.name,
                  })
                }>
                {provider.label}
              </Button>
            );
          })}
          <Divider>
            <Text
              style={{
                color: token.colorTextLabel,
              }}>
              {translate('pages.login.divider', 'or')}
            </Text>
          </Divider>
        </>
      );
    }

    return null;
  };

  const CardContent = (
    <Card
      title={CardTitle}
      styles={{ header: headStyles, body: bodyStyles }}
      style={{
        ...containerStyles,
        backgroundColor: token.colorBgElevated,
      }}
      {...(contentProps ?? {})}>
      {renderProviders()}
      <Form<LoginFormTypes>
        layout="vertical"
        form={form}
        onFinish={values => login(values)}
        requiredMark={false}
        initialValues={{
          remember: false,
        }}
        {...formProps}>
        <Form.Item
          name="username"
          label={translate('pages.login.fields.username', 'Username')}
          rules={[{ required: true, message: translate('pages.login.errors.validUsername') }]}>
          <Input size="large" placeholder={translate('pages.login.fields.username', 'Username')} />
        </Form.Item>
        <Form.Item
          name="password"
          label={translate('pages.login.fields.password', 'Password')}
          rules={[{ required: true, message: translate('pages.login.errors.validUsername') }]}>
          <Input type="password" placeholder="●●●●●●●●" size="large" />
        </Form.Item>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '24px',
          }}></div>
        <Form.Item>
          <Button type="primary" size="large" htmlType="submit" block>
            {translate('pages.login.signin', 'Sign in')}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );

  return (
    <Layout style={{ ...layoutStyles, background: 'none' }} {...(wrapperProps ?? {})}>
      <Row
        justify="center"
        align="middle"
        style={{
          height: '100dvh',
        }}>
        <Col xs={22}>
          {renderContent ? (
            renderContent(CardContent, PageTitle)
          ) : (
            <>
              {PageTitle}
              {CardContent}
            </>
          )}
        </Col>
      </Row>
    </Layout>
  );
};
